import { inject, injectable } from 'inversify';
import type { Vector3 } from 'three';
import type { IRequestSliceStrategy } from './Images.service';
import type { ImagesInfo } from './ImagesInfo';
import type { ImageStackController } from './ImageStackController';
import { IRequestSliceStrategiesProvider } from './interfaces';
import type { SliceType } from './interfaces';
import { ImagesMode } from './interfaces';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import { findNearestCenterlinePointToPoint, findNearestPolylinePointToPlane } from '@/engine/utils/geometryHelper';
import * as THREE from 'three';
import { TYPES } from '@/engine/ioc';
import type { ViewsService } from '@/engine/app/Views.service.ts';

@injectable()
export class RequestSliceStrategiesProvider implements IRequestSliceStrategiesProvider {
  private _viewsService: ViewsService;

  public constructor(@inject(TYPES.ViewsService) viewsService: ViewsService) {
    this._viewsService = viewsService;
  }

  public getStrategy(
    imagesMode: ImagesMode,
    imageStackController: ImageStackController,
    imageOrientationInfos: Map<SliceType, ImagesInfo>
  ): IRequestSliceStrategy {
    switch (imagesMode) {
      case ImagesMode.MPR: {
        return new MprStrategy(imageStackController);
      }
      case ImagesMode.POLYLINE: {
        return new PolylineStrategy(
          imageStackController,
          imageOrientationInfos,
          this._viewsService.currentViewOrThrow.isAxisCenterlineMode
        );
      }
      default: {
        throw new Error('Inappropriate Images Mode');
      }
    }
  }
}

class MprStrategy implements IRequestSliceStrategy {
  private readonly _imageStackController: ImageStackController;

  public constructor(imageStackController: ImageStackController) {
    this._imageStackController = imageStackController;
  }

  public requestSlice(sliceType: SliceType, index: number): void {
    this._imageStackController.requestSliceByIndex(sliceType, index);
  }

  public requestSliceByPoint(sliceType: SliceType, coplanarPoint: Vector3): void {
    this._imageStackController.requestSliceByPoint(sliceType, coplanarPoint);
  }

  public syncSliceWithPlane(
    slice: SliceType,
    origin: Vector3,
    normal: Vector3,
    orientationY?: Vector3,
    excludedSlice?: SliceType,
    isDeltaTransformation = false
  ): void {
    return this._imageStackController.alignWithPlane(
      slice,
      origin,
      normal,
      orientationY,
      excludedSlice,
      isDeltaTransformation
    );
  }

  public dispose(): void {
    // noop
  }
}

class PolylineStrategy implements IRequestSliceStrategy {
  private readonly _isAxisCenterlineMode: boolean;

  private readonly _imageStackController: ImageStackController;

  private readonly _imageOrientationInfos: Map<SliceType, ImagesInfo> = new Map();

  private _imageStackSubscriptions: DisposablesBasket = new DisposablesBasket();

  public constructor(
    imageStackController: ImageStackController,
    imageOrientationInfos: Map<SliceType, ImagesInfo>,
    isAxisCenterlineMode: boolean
  ) {
    this._imageStackController = imageStackController;
    this._imageOrientationInfos = imageOrientationInfos;
    this._isAxisCenterlineMode = isAxisCenterlineMode;
    imageStackController.getRegisteredSliceTypes().forEach((sliceType) => {
      this._imageStackSubscriptions.add(
        imageStackController.addOnPositionChangedListener(sliceType, (sliceContent) =>
          this._setCurrentSlice(sliceType, sliceContent.sliceCS)
        )
      );
    });
  }

  public requestSlice(sliceType: SliceType, index: number): void {
    const polylineInfo = this._imageOrientationInfos.get(sliceType)?.polylineInfo;
    if (!polylineInfo) {
      throw new Error('Polyline info is undefined');
    }
    if (polylineInfo.slicesCount === 0) return;
    const origin = polylineInfo.polylinePoints[index];
    const normal = polylineInfo.polylineNormals[index];

    if (this._isAxisCenterlineMode) {
      this._imageStackController.requestSliceByPoint(sliceType, origin);
    } else {
      this._imageStackController.alignWithPlane(sliceType, origin, normal, undefined, undefined, true);
    }
  }

  public requestSliceByPoint(sliceType: SliceType, coplanarPoint: Vector3): void {
    const polylineInfo = this._imageOrientationInfos.get(sliceType)?.polylineInfo;
    if (!polylineInfo) {
      throw new Error('Polyline info is undefined');
    }
    const idx = findNearestCenterlinePointToPoint(
      polylineInfo.polylinePoints,
      polylineInfo.polylineNormals,
      coplanarPoint
    );
    const origin = polylineInfo.polylinePoints[idx];
    const normal = polylineInfo.polylineNormals[idx];
    this._imageStackController.alignWithPlane(sliceType, origin, normal, undefined, undefined, true);
  }

  public syncSliceWithPlane(
    slice: SliceType,
    origin: Vector3,
    normal: Vector3,
    orientationY?: Vector3,
    excludedSlice?: SliceType,
    isDeltaTransformation = false
  ): void {
    const polylineInfo = this._imageOrientationInfos.get(slice)?.polylineInfo;
    if (!polylineInfo) {
      console.warn('Empty polylineInfo');
      return;
    }
    const idx = findNearestPolylinePointToPlane(polylineInfo.polylinePoints, normal, origin);
    if (idx == -1) {
      console.warn('Point on polyline was not found');
      return;
    }
    const origin2 = polylineInfo.polylinePoints.at(idx);
    const normal2 = polylineInfo.polylineNormals.at(idx);
    if (!origin2 || !normal2) {
      console.warn('Polyline index out of range');
      return;
    }
    if (normal2.distanceTo(normal) > 0.01) {
      console.warn('Discrepancy between plane and centerline');
    }
    return this._imageStackController.alignWithPlane(
      slice,
      origin2,
      normal2,
      orientationY,
      excludedSlice,
      isDeltaTransformation
    );
  }

  public dispose(): void {
    this._imageStackSubscriptions.dispose();
  }

  private _setCurrentSlice(sliceType: SliceType, sliceCS: THREE.Matrix4): void {
    const polylineInfo = this._imageOrientationInfos.get(sliceType)?.polylineInfo;
    if (polylineInfo) {
      const sliceO = new THREE.Vector3().setFromMatrixPosition(sliceCS);
      const sliceZ = new THREE.Vector3().setFromMatrixColumn(sliceCS, 2);
      const idx = findNearestPolylinePointToPlane(polylineInfo.polylinePoints, sliceZ, sliceO);
      polylineInfo.setCurrentSlice(idx);
    }
  }
}
